import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import { Route, HashRouter as Router, Switch } from 'react-router-dom';
import { i18nIntl, isDesktop, isNotEmpty } from '../../lib/ep.js';
import { v4 as uuidv4 } from 'uuid';

import ActionButton from '../builder/ActionButton.js';
import BuilderCanvas from '../builder/BuilderCanvas.js';
import BuilderMain from '../builder/BuilderMain.js';
import BuilderSidebar from '../builder/BuilderSidebar.js';
import ButtonCollection from '../builder/ButtonCollection.js';
import Checkbox from '../builder/Checkbox.js';
import { AutoPostImageUploader } from '../builder/ImageUploader.js';
import Input from '../builder/Input.js';
import Model from '../builder/Model.js';
import { NavigationTabsBuilder } from '../builder/NavigationTabs.js';
import Range from '../builder/Range.js';
import Select from '../builder/Select.js';

import { GameContainerWrapper } from '../front/GameContainer.js';
import Layout from '../front/Layout.js';
import PenaltyClient from '../front/PenaltyClient.js';
import GameInstructionsBuilder from './GameInstructionsBuilder.js';
import GameScoreBuilder from './GameScoreBuilder.js';
import GameSoundtrackFields from '../builder/GameSoundtrackFields.js';
import ColorPicker from '../builder/ColorPicker.js';
import ColorPickerFromPalette from '../builder/ColorPickerFromPalette.js';
import BuilderSubSectionTitle from './BuilderSubSectionTitle.js';
import Tooltip from '../builder/Tooltip.js';
import DynamicFields from '../builder/DynamicFields.js';
import FloatingLayer from '../builder/FloatingLayer.js';
import ActionLink from '../builder/ActionLink.js';
import SectionTitle from '../builder/SectionTitle.js';
import SectionSubtitle from '../builder/SectionSubtitle.js';

const GameSettings = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ errors_game, setErrorsGame ] = useState({});

	const option_mode = [
		{ k:'real', v:i18n("Real game") },
		{ k:'foosball', v:i18n("Foosball") },
	];

	const option_level = [
		{ k:'easy', v:i18n("Noob") },
		{ k:'normal', v:i18n("Normal") },
		{ k:'hard', v:i18n("Pro") },
	];

	const option_barrier_movement = [
		{ k:'free', v:i18n("Free movement") },
		{ k:'barrier', v:i18n("Wall") },
	];

	const option_shot_path = [
		{ k:'full', v:i18n("Full") },
		{ k:'normal', v:i18n("***Normal") },
		{ k:'short', v:i18n("Short") },
		{ k:'none', v:i18n("None") },
	];

	const option_heisenberg = [
		{ k:'no', v:i18n("No effect") },
		{ k:'weak', v:i18n("Weak") },
		{ k:'normal', v:i18n("***Normal") },
		{ k:'intense', v:i18n("Intense") },
	];

	const option_points_for_fail = [{ k:'0', v:"Score is not deduced" }];
	const points_for_fail_mod = 5;
	[...Array(5).keys()].map((foo, i) => {
		const p = points_for_fail_mod * (i + 1);
		option_points_for_fail.push({ k:p, v:`${props.intl.formatMessage({ id:"Deduce {total} points" }, { total:p })}` });
	});

	const penaltyChange = (k, v) => {
		if(k == 'puzzle_level') {
			let attempts = 9;
			if(v == 'normal') attempts = 16;
			if(v == 'hard') attempts = 25;
			
			if(stage?.game_attempts != undefined && stage?.game_attempts < attempts ) {
				change('game_attempts', attempts);
			}
		}

		change(k, v);
	};

	const mode = stage?.penalty_mode ?? 'real';

	return (
		<>
			<BuilderSidebar
				onSave={props.saveStage}
				errors={(Object.keys(errors_game).length > 0) ? true : false}
			>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<Model model={stage} change={penaltyChange}>
						<Range
							min={30}
							max={300}
							step={5}
							label_props={{
								addition:"In seconds",
							}}
							name="game_time"
							value={Math.max((stage?.game_time ?? 60), 30)}
							_class={{ first:true }}
						>Time limit</Range>

						<Select
							name="penalty_mode"
							values={option_mode}
							value={mode}
							_class={{ size:50 }}
						>Game mode</Select>

						<Range
							min={1}
							max={10}
							step={1}
							name="penalty_num_goals"
							value={Math.max((parseInt(stage?.penalty_num_goals ?? '1')), 1)}
							_class={{ size:60 }}
						>Number of goals</Range>

						<Select
							name="penalty_points_for_fail"
							values={option_points_for_fail}
							value={parseInt(stage?.penalty_points_for_fail ?? '0')}
							_class={{ size:50 }}
						>If shot is missed...</Select>
					</Model>
				</article>

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Opponents"
						icon='knk-videogame'
					/>

					<Model model={stage} change={penaltyChange}>
						<Select
							_class={{ size:50 }}
							name="penalty_level"
							values={option_level}
							value={stage?.penalty_level ?? 'normal'}
						>Skill level</Select>

						<Range
							name="penalty_barrier_length"
							min={0}
							max={3}
							step={1}
							value={Math.min((stage?.penalty_barrier_length ?? 0), 4)}
							_class={{ size:60 }}
						>Number of defenders</Range>

						{(mode != 'foosball' && (stage?.penalty_barrier_length ?? 0) > 0) && (
							<Select
								name="penalty_move_barrier"
								values={option_barrier_movement}
								value={stage?.penalty_move_barrier ?? 'free'}
								_class={{ size:50 }}
							>Defense mode</Select>
						)}
					</Model>
				</article>

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Scorer"
						icon='knk-focus-point'
					/>

					<Model model={stage} change={penaltyChange}>
						<Checkbox
							name="penalty_relocate_ball"
						>Change initial ball position after each shot</Checkbox>

						<Select
							name="game_display_path"
							values={option_shot_path}
							value={stage?.game_display_path ?? 'full'}
							_class={{ size:50 }}
						>Display trajectory</Select>

						{((stage?.game_display_path ?? 'full') != 'none') && (
							<Select
								name="game_apply_heisenberg"
								values={option_heisenberg}
								value={stage?.game_apply_heisenberg ?? 'normal'}
								label_props={{
									tooltip:<Tooltip
										autotranslate={false}
										position='top'
									>{`${i18n("The more power, the less accuracy.")}<br /><br />${props.intl.formatMessage({ id:"The ball's trajectory widens like a cone, reflecting the uncertainty of the shot. Inspired by the {principle} of {author}, a German physicist and Nobel Prize winner in 1932." }, { principle:`<strong>${i18n("Uncertainty Principle")}</strong>`, author:"<strong>Werner Heisenberg</strong>" })}<br /><br />${i18n("The faster you go, the less you know where you'll end up!")}`}</Tooltip>,
								}}
								_class={{ size:50 }}
							>Heisenberg effect</Select>
						)}
					</Model>
				</article>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{width:'100%'}}>
						{(Object.keys(errors_game).length == 0) ? (
							<PenaltyClient
								stage={{ Stage:stage }}
								is_builder={true}
								is_playable={true}
								section="settings"
								key={stage.puzzle_level}
							/>
						) : (
							<GameContainerWrapper>
								<div class="is_fullscreen relative_to_container empty_game_warning not_awesome orangeBg knk-arrow-l-big">
									{Object.values(errors_game).map((error, i) => {
										if(typeof error === 'object') return null;

										return (
											<p key={`error${i}`}>{error}</p>
										)
									})}
								</div>
							</GameContainerWrapper>
						)}
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const ReactionTextField = ({ model, change, ...props }) => {
	return (
		<Model
			model={model}
			change={change}
			errors={props.errors != undefined && props.errors[model.id]}
		>
			<Input
				name='title'
				max_length={15}
				_class={{ size:70 }}
			>Text</Input>
		</Model>
	)
};

const GameDesign = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ display_jersey_template_layer, setDisplayJerseyTemplateLayer] = useState(false);

	const mode = stage?.penalty_mode ?? 'real';
	const genre = stage?.penalty_team_genre ?? 'male';

	const skin_colors = ['#E9C085', '#C8A069', '#997650', '#56422E', '#35281C'];
	const hair_colors = ['#C1BBB6', '#C4BE74', '#9B7E37', '#645041', '#3B2E28', '#15110F'];

	const message_type_subtitles = {
		goal:"***Goal",
		super_goal:"Great goal",
		blocked:"***Save",
		super_blocked:"Great save",
		barrier:"Wall",
		post:"Post",
		out:"Out",
	};

	const default_messages = [{
		id:uuidv4(),
		title:"Goal!",
		type:'goal',
	}, {
		id:uuidv4(),
		title:"Epic goal!",
		type:'super_goal',
	}, {
		id:uuidv4(),
		title:"Great save!",
		type:'blocked',
	}, {
		id:uuidv4(),
		title:"Amazing save!",
		type:'super_blocked',
	}, {
		id:uuidv4(),
		title:"To the barrier!",
		type:'barrier',
	}, {
		id:uuidv4(),
		title:"Off the post!",
		type:'post',
	}, {
		id:uuidv4(),
		title:"Missed!",
		type:'out',
	}];

	const reaction_texts = stage?.penalty_reaction_messages ?? default_messages;

	// ensure we have at least 1 of all the possible types created:
	default_messages.map((type) => {
		const total = reaction_texts.find((t) => (t.type == type.type));
		if(total == undefined) {
			const missing_type = default_messages.find((t) => (t.type == type.type));
			reaction_texts.push(missing_type);
		}
	});

	const changeReactionMessages = (v) => {
		const ordering = { goal:1, super_goal:2, blocked:3, super_blocked:4, barrier:5, post:6, out:7 };

		const sorted_reaction_texts = v.sort((a, b) => {
			return (ordering[a.type] < ordering[b.type]) ? -1 : 1;
		});

		change('penalty_reaction_messages', sorted_reaction_texts);
	};

	const total_messages = reaction_texts.filter((m) => {
		if(mode != 'foosball') return true;
		return (['super_goal', 'super_blocked'].includes(m.type)) ? false : true;
	}).length;
	const messages_title = `${i18n("Reaction messages")} (${total_messages})`;

	const default_type_props = {
		min:1,
		max:5,
		translate:[{
			key:'title',
			type:'input',
		}],
		title:(f) => {
			return (isNotEmpty(f, 'title')) ? f.title : `Message`;
		},
		subtitle:(f) => {
			return i18n(message_type_subtitles[f.type]);
		},
	};

	const reactions_fields = [{
		type:'goal',
		label:message_type_subtitles.goal,
		icon:'knk-football-goal',
		...default_type_props,
	}];

	if(mode == 'real') {
		reactions_fields.push({
			type:'super_goal',
			label:message_type_subtitles.super_goal,
			icon:'knk-football-goal',
			...default_type_props,
		});
	}

	reactions_fields.push({
		type:'blocked',
		label:message_type_subtitles.blocked,
		icon:'knk-zombie-hand',
		...default_type_props,
	});

	if(mode == 'real') {
		reactions_fields.push({
			type:'super_blocked',
			label:message_type_subtitles.super_blocked,
			icon:'knk-zombie-hand',
			...default_type_props,
		});
	}
	
	reactions_fields.push({
		type:'barrier',
		label:message_type_subtitles.barrier,
		icon:'knk-blocked',
		...default_type_props,
	});
	
	reactions_fields.push({
		type:'post',
		label:message_type_subtitles.post,
		icon:'knk-alert',
		...default_type_props,
	});

	reactions_fields.push({
		type:'out',
		label:message_type_subtitles.out,
		icon:'knk-view',
		...default_type_props,
	});

	const avail_locales = [];
	if(window.langs != undefined) {
		window.langs.map((loc) => {
			avail_locales.push(loc.I18nPromotion.lang);
		})
	}

	const genres = [
		{ k:'male', v:"***Male" },
		{ k:'female', v:"***Female" },
	];

	return (
		<>
			<BuilderSidebar
				onSave={props.saveStage}
			>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<Model model={stage} change={change}>
						<Select
							_class={{ size:60, first:true }}
							name="penalty_team_genre"
							values={genres}
							value={genre}
						>Team</Select>
					</Model>
				</article>

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Goalkeeper"
						icon='knk-football-goal'
					/>

					<Model model={stage} change={change}>
						<ColorPicker
							name="penalty_goal_keeper_shirt_color"
							color={stage?.penalty_goal_keeper_shirt_color ?? 'rgba(150, 70, 147, 1)'}
							_class={{ size:50 }}
						>Jersey color</ColorPicker>

						<ColorPicker
							name="penalty_goal_keeper_pants_color"
							color={stage?.penalty_goal_keeper_pants_color ?? 'rgba(50, 40, 130, 1)'}
							_class={{ size:50 }}
						>Shorts color</ColorPicker>

						<ColorPicker
							name="penalty_goal_keeper_glove_color"
							color={stage?.penalty_goal_keeper_glove_color ?? 'rgba(255, 255, 255, 1)'}
							_class={{ size:50 }}
						>Gloves color</ColorPicker>

						<ColorPicker
							name="penalty_goal_keeper_boot_color"
							color={stage?.penalty_goal_keeper_boot_color ?? 'rgba(220, 220, 60, 1)'}
							_class={{ size:50 }}
						>Footwear color</ColorPicker>

						<ColorPickerFromPalette
							name="penalty_goal_keeper_skin_color"
							value={stage?.penalty_goal_keeper_skin_color ?? 'random'}
							palette={skin_colors}
							enable_random={true}
						>Skin color</ColorPickerFromPalette>

						<ColorPickerFromPalette
							name="penalty_goal_keeper_hair_color"
							value={stage?.penalty_goal_keeper_hair_color ?? 'random'}
							palette={hair_colors}
							enable_random={true}
							enable_empty={(genre == 'male') ? true : false}
						>Hair color</ColorPickerFromPalette>
					</Model>
				</article>

				{((stage?.penalty_barrier_length ?? 0) > 0) && (
					<article class="gui_not_in_top_huge">
						<BuilderSubSectionTitle
							title="Defenders"
							icon='knk-blocked'
						/>

						<Model model={stage} change={change}>
							<AutoPostImageUploader
								auto_url={`/promotions/auto_img/${props.promotion.id}`}
								auto_type='stages'
								name="penalty_shirt_barrier_image"
								image={stage.full_penalty_shirt_barrier_image}
								display_custom_dragging_instructions="Upload the image"
								small_input={true}
								_size={{ width:168, height:84 }}
								label_props={{
									_extra_component:(isDesktop()) ? <ActionLink onClick={() => setDisplayJerseyTemplateLayer(true)}>Info</ActionLink> : null,
								}}
							>Jersey image</AutoPostImageUploader>

							<ColorPicker
								name="penalty_barrier_shirt_color"
								color={stage?.penalty_barrier_shirt_color ?? 'rgba(150, 70, 147, 1)'}
								_class={{ size:50 }}
							>Jersey color</ColorPicker>

							<ColorPicker
								name="penalty_barrier_pants_color"
								color={stage?.penalty_barrier_pants_color ?? 'rgba(50, 40, 130, 1)'}
								_class={{ size:50 }}
							>Shorts color</ColorPicker>

							<ColorPicker
								name="penalty_barrier_boot_color"
								color={stage?.penalty_barrier_boot_color ?? 'rgba(220, 220, 60, 1)'}
								_class={{ size:50 }}
							>Footwear color</ColorPicker>

							<ColorPickerFromPalette
								name="penalty_barrier_skin_color"
								value={stage?.penalty_barrier_skin_color ?? 'random'}
								palette={skin_colors}
								enable_random={true}
							>Skin color</ColorPickerFromPalette>

							<ColorPickerFromPalette
								name="penalty_barrier_hair_color"
								value={stage?.penalty_barrier_hair_color ?? 'random'}
								palette={hair_colors}
								enable_random={true}
								enable_empty={(genre == 'male') ? true : false}
							>Hair color</ColorPickerFromPalette>
						</Model>
					</article>
				)}

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Field"
						icon='knk-football-play-field'
					/>

					<Model model={stage} change={change}>
						<Checkbox
							name="display_outter_border"
							value={stage?.display_outter_border ?? true}
						>Display outer border</Checkbox>

						<ColorPicker
							name='penalty_field_lines_color'
							color={stage?.penalty_field_lines_color ?? 'rgba(255, 255, 255, 1)'}
							_class={{ size:50 }}
						>Field lines color</ColorPicker>

						{(mode != 'foosball') && (
							<Checkbox
								name="penalty_display_field_texture"
								value={stage?.penalty_display_field_texture ?? true}
							>Display field texture</Checkbox>
						)}

						<ColorPicker
							name='bg_color'
							color={stage?.bg_color ?? ((mode == 'real') ? 'rgba(90, 150, 50, 1)' : 'rgba(60, 95, 45, 1)')}
							_class={{ size:50 }}
						>Background color</ColorPicker>

						<ColorPicker
							name='shooter_color'
							color={stage?.shooter_color ?? 'rgba(255, 255, 255, 1)'}
							_class={{ size:50 }}
						>Scorer color</ColorPicker>

						{(mode == 'foosball') && (
							<ColorPicker
								name='penalty_foosball_cover_color'
								color={stage?.penalty_foosball_cover_color ?? 'rgba(160, 85, 20, 1)'}
								_class={{ size:50 }}
							>Cover color</ColorPicker>
						)}

						<AutoPostImageUploader
							auto_url={`/promotions/auto_img/${props.promotion.id}`}
							auto_type='stages'
							name="penalty_ball_image"
							image={stage.full_penalty_ball_image}
							display_custom_dragging_instructions="Upload the image"
							small_input={true}
							_class={{ size:40 }}
							_size={{ width:120, height:120 }}
						>Ball</AutoPostImageUploader>

						<AutoPostImageUploader
							auto_url={`/promotions/auto_img/${props.promotion.id}`}
							auto_type='stages'
							name="penalty_left_logo"
							image={stage.full_penalty_left_logo}
							display_custom_dragging_instructions="Upload the image"
							small_input={true}
							_class={{ size:40 }}
							_size={{ width:300, height:300 }}
						>Left logo</AutoPostImageUploader>

						<AutoPostImageUploader
							auto_url={`/promotions/auto_img/${props.promotion.id}`}
							auto_type='stages'
							name="penalty_right_logo"
							image={stage.full_penalty_right_logo}
							display_custom_dragging_instructions="Upload the image"
							small_input={true}
							_class={{ size:40 }}
							_size={{ width:300, height:300 }}
						>Right logo</AutoPostImageUploader>
					</Model>
				</article>

				<article class="gui_not_in_top_huge">
					<DynamicFields
						section_title={messages_title}
						onChange={(v) => changeReactionMessages(v)}
						field_types={reactions_fields}
						section_icon='knk-comment'
						has_translation={true}
						sortable={false}
						can_copy={false}
						locales={avail_locales}
						add_field_label="Add"
						add_button_props={{
							icon:'knk-add-item mainButton',
						}}
						ul_classes='gui_overflowable'
						ul_styles={{ maxHeight:'calc(100dvh - 285px)' }}
						fields={stage.penalty_reaction_messages}
					>
						<ReactionTextField />
					</DynamicFields>
				</article>

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Sound FX"
						icon='knk-music'
					/>

					<Model model={stage} change={change}>
						<Checkbox
							name='silence'
						>Disable sounds</Checkbox>

						{(mode == 'real' && (stage?.silence ?? false) === false) && (
							<Checkbox
								name='penalty_sound_morse'
								label_props={{
									tooltip:<Tooltip
										autotranslate={false}
										position='top'
									>Tiii-tiii-ti tiii-tiii-tiii ti-tiii-ti-ti</Tooltip>,
								}}
								value={stage?.penalty_sound_morse ?? true}
							>Add G-O-L morse sound when scoring</Checkbox>
						)}
					</Model>

					<GameSoundtrackFields
						stage={stage}
						change={change}
					/>
				</article>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{width:'100%'}}>
						<PenaltyClient
							stage={{ Stage:stage }}
							is_builder={true}
							is_playable={true}
							section="design"
						/>
					</div>
				</Layout>

				{(display_jersey_template_layer) && (
					<FloatingLayer
						backButton={() => setDisplayJerseyTemplateLayer(false)}
						_class={{ size:'small' }}
					>
						<div class="without_actions">
							<SectionTitle>Template image</SectionTitle>

							<SectionSubtitle>You can download this PNG image and use it as a template to create your own.</SectionSubtitle>

							<div class="gui_flexbox_auto gui_flex_center gui_not_in_top_small">
								<img
									src={`${window.cdn_url}img/games_gui/free-kick/jersey-img-template.png`}
									width={350}
									alt="Jersey template"
								/>
							</div>
						</div>
					</FloatingLayer>
				)}
			</BuilderCanvas>
		</>
	)
};

const GameTest = ({ stage, change, ...props }) => {
	const restart = () => {
		change('reset', (stage.reset != undefined) ? (stage.reset + 1) : 0);
	};

	return (
		<>
			<BuilderSidebar
				onSave={props.saveStage}
			>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<ButtonCollection>
					<ActionButton
						icon='knk-reload'
						onClick={() => restart()}
					>Restart game</ActionButton>
				</ButtonCollection>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{width:'100%'}}>
						<PenaltyClient
							stage={{ Stage:stage }}
							is_builder={true}
							is_playable={true}
							is_test={true}
						/>
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const PenaltyBuilder = ({ base_route, ...props }) => {
	const basename = `/stages/${props.stage.id}/penalty`;
	const nav_sections = [
		{ label:"Go back", link:`/stages/${props.stage.id}/options`, icon:'knk-go-prev' },
		{ label:"Gameplay settings", link:`${basename}/settings`, icon:'knk-settings' },
		{ label:"Design", link:`${basename}/design`, icon:'knk-artist-mode' },
		{ label:"Instructions", link:`${basename}/instructions`, icon:'knk-help' },
		{ label:"Score screen", link:`${basename}/gamescore`, icon:'knk-like' },
		{ label:"Test the game", link:`${basename}/test`, icon:'knk-ok' },
	];

	const stage = props.stage;

	const common_props = {
		stage,
		is_multistage:props.is_multistage,
		isCorporate:props.isCorporate,
		change:props.change,
		changeMulti:props.changeMulti,
		intl:props.intl,
		promotion:props.promotion,
		promoModel:props.promoModel,
		saveStage:props.saveStage,
		stage_indicator:props.stage_indicator,
		nav_sections:nav_sections,
	};

	return (
		<BuilderMain pm={props.promoModel}>
			<Router basename={basename} hashType="noslash">
				<Switch>
					<Route exact={true} path="/settings">
						<GameSettings
							{...common_props}
							nav_active={`/settings`}
						/>
					</Route>

					<Route exact={true} path="/design">
						<GameDesign
							{...common_props}
							nav_active={`/design`}
						/>
					</Route>

					<Route exact={true} path="/instructions">
						<GameInstructionsBuilder
							{...common_props}
							nav_active={`/instructions`}
						>
							<PenaltyClient
								stage={{ Stage:props.stage }}
								promotion={props.promotion}
								is_builder={true}
								section="instructions"
							/>
						</GameInstructionsBuilder>
					</Route>

					<Route exact={true} path="/gamescore">
						<GameScoreBuilder
							{...common_props}
							nav_active={`/gamescore`}
							exclude_cases={['fail', 'out_of_moves', 'out_of_time']}
						>
							<PenaltyClient
								stage={{ Stage:props.stage }}
								promotion={props.promotion}
								is_builder={true}
								section="score"
							/>
						</GameScoreBuilder>
					</Route>

					<Route exact={true} path="/test">
						<GameTest
							{...common_props}
							nav_active={`/test`}
						/>
					</Route>
				</Switch>
			</Router>
		</BuilderMain>
	)
}
export default injectIntl(PenaltyBuilder);